import React, { useState, useEffect,useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import { Box, Chip } from '@mui/material';
import FilterTable from '../../../../atoms/TableComponent/FilterTable/FilterTable';
import axiosInstance from '../../../../lib/axios';

export default function HKTypeTable() {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  const [tappedRowId, setTappedRowId] = useState(null);
  const [lastTap, setLastTap] = useState(0);
  const DOUBLE_TAP_DELAY = 300;
  const isMobile = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const permissions = usePermissions("House Keeping Type Master");
  const { AddRec = "0" } = permissions || {};

  useEffect(() => {
    fetchHkTypeData();
  }, []);

  const fetchHkTypeData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post('MstHkType/getAllMstHkTypeDashBoard', {
        start: 0,
        PageSize: 0,
        SearchText: ""
      },);
      const { data: { Status, Data } } = response;
      if (Status === 0) {
        const formattedData = Data.map(hkType => ({
          ...hkType,
          RowNum: hkType.RowNum || '',
          HkTypeName: hkType.HkTypeName || '',
          Abrv: hkType.Abrv || '',
          Remark: hkType.Remark,
          Status: hkType.Status === null ? '' : (hkType.Status == "1" ? 'Active' : 'Inactive'),
        }));

        setRows(formattedData);
      } else {
        console.error('Error fetching housekeepingtype data:', response.data.Message);
        setRows([]);
      }
    } catch (error) {
      console.error('Error fetching housekeepingtype data:', error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleCellClicked = useCallback(
    (params) => {
      if (!params.data?.HkTypeId) return;

      const id = params.data.HkTypeId;

      // Visual feedback
      setTappedRowId(id);
      setTimeout(() => setTappedRowId(null), 300);

      const now = Date.now();
      const timeSince = now - lastTap;

      // Mobile → single tap
      if (isMobile()) {
        navigate('/hk-type', { state: { HkTypeId: id, mode: 'view' } });
        return;
      }

      // Desktop → double-click fallback
      if (timeSince > 0 && timeSince < DOUBLE_TAP_DELAY) {
        navigate('/hk-type', { state: { HkTypeId: id, mode: 'view' } });
      }
      setLastTap(now);
    },
    [navigate, lastTap]
  );
  const getRowStyle = (params) => {
    if (tappedRowId === params.data?.HkTypeId) {
      return { background: '#bbdefb', transition: 'background 0.2s' };
    }
    return null;
  };

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      handleClick();
    }
  };

  const handleClick = () => {
    navigate('/hk-type', { state: { mode: "add" } });
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleLocationclick = () => {
    navigate('/masters/operations');
  };

  const handleRowDoubleClick = (params) => {
    const hkTypeId = params?.data?.HkTypeId;
    if (hkTypeId) {
      navigate('/hk-type', { state: { HkTypeId: hkTypeId, mode: 'view' } });
    }
  };

  const TruncatedCellRenderer = (props) => {
    const [isOverflowed, setIsOverflowed] = useState(false);
    const cellRef = React.useRef(null);

    useEffect(() => {
      if (cellRef.current) {
        const isTextOverflowed =
          cellRef.current.scrollWidth > cellRef.current.clientWidth ||
          (props.value && props.value.length > 20);
        setIsOverflowed(isTextOverflowed);
      }
    }, [props.value]);

    const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

    return (
      <div
        ref={cellRef}
        className="truncated-cell"
        style={{ width: '100%' }}
      >
        {cellValue}
      </div>
    );
  };

  const StatusCellRenderer = (props) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'Active':
          return '#4caf50';
        case 'Inactive':
          return '#f44336';
        default:
          return '#f44336';
      }
    };

    return (
      <Chip
        label={props.value}
        className="status-chip"
        sx={{
          backgroundColor: getStatusColor(props.value),
          color: 'white',
          width: '100%',
          maxWidth: '100px',
        }}
      />
    );
  };

  const columnDefs = [
    { headerName: 'Sr.No.', field: 'RowNum', width: 60, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'HkTypeName', field: 'HkTypeName', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Abrv', field: 'Abrv', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Remark', field: 'Remark', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Status', field: 'Status', width: 100, filter: 'agTextColumnFilter', cellRenderer: StatusCellRenderer },
  ];

  return (
    <Box sx={{ height: '90vh' }}>
      <FilterTable
        columnDefs={columnDefs}
        rowData={rows}
        onRowDoubleClicked={handleRowDoubleClick}
        isLoading={isLoading}
        onHomeClick={handleHomeClick}
        onParentClick={handleLocationclick}
        breadcrumbParentLabel="Operations"
        breadcrumbCurrentLabel="Work Type"
        onAddClick={handlePermittedClick}
        hasAddPermission={AddRec === "1"}
        onCellClicked={handleCellClicked}
        getRowStyle={getRowStyle}
      />
    </Box>
  );
}


